import React, { useEffect, useState } from 'react';
import { X, Send, Loader2, Paperclip, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import { enviarLclOferta } from '../lclOfertas.service.js';

const inputCls =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const listaCorreos = (s) =>
  String(s || '')
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const esCorreo = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);

/**
 * Envío de la oferta al cliente, con el PDF adjunto.
 *
 * El destinatario se prellena con el contacto del cliente (el "ATN"),
 * que es a quien va dirigida la cotización.
 */
export default function EnviarOfertaDialog({ open, onOpenChange, oferta, onSent }) {
  const toast = useToast();
  const [para, setPara] = useState('');
  const [cc, setCc] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open || !oferta) return;
    setPara(oferta.contactoSnapshot?.correo || oferta.clienteSnapshot?.correo || '');
    setCc(oferta.asesorSnapshot?.correo || '');
    setAsunto(`Cotización ${oferta.numero}`);
    setMensaje('');
  }, [open, oferta]);

  const destinatarios = listaCorreos(para);
  const copias = listaCorreos(cc);
  const invalidos = [...destinatarios, ...copias].filter((x) => !esCorreo(x));
  const puedeEnviar = destinatarios.length > 0 && invalidos.length === 0;

  const enviar = async () => {
    setEnviando(true);
    try {
      const r = await enviarLclOferta(oferta._id, {
        para: destinatarios,
        cc: copias,
        asunto: asunto || undefined,
        mensaje: mensaje || undefined,
      });
      if (r?.success) {
        // El backend distingue el envío real del dry-run y lo dice en el
        // mensaje; se muestra tal cual para no dar por enviado algo que
        // no salió.
        toast.success('Listo', r.message);
        onSent?.();
        onOpenChange?.(false);
      } else {
        toast.error('No se pudo enviar', [].concat(r?.message || '').join(' · '));
      }
    } catch (e) {
      toast.error('No se pudo enviar', e?.message);
    } finally {
      setEnviando(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange?.(false)}>
      {/*
        Usa el `Dialog` del proyecto, no un overlay propio: este modal se
        abre desde adentro del detalle de la solicitud, que es un
        `DialogContent` de Radix con `transform`. Un ancestro transformado
        pasa a ser el bloque contenedor de los `position: fixed` de adentro,
        así que `inset-0` medía contra esa tarjeta en vez de contra la
        pantalla y el `overflow-hidden` del padre recortaba encabezado y pie.
      */}
      <DialogContent hideClose className="max-w-lg gap-0 p-0">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h3 className="text-lg font-semibold">Enviar al cliente</h3>
            <p className="text-sm text-muted-foreground">
              {oferta?.numero} · {oferta?.clienteSnapshot?.nombre}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange?.(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-3 p-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Para</label>
            <input
              className={inputCls}
              value={para}
              placeholder="cliente@empresa.com"
              onChange={(e) => setPara(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Copia (opcional)
            </label>
            <input className={inputCls} value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Asunto</label>
            <input
              className={inputCls}
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Mensaje (opcional)
            </label>
            <textarea
              rows={4}
              className={cn(inputCls, 'h-auto py-2')}
              value={mensaje}
              placeholder="Si lo dejás vacío se usa un texto por defecto con la ruta y la vigencia."
              onChange={(e) => setMensaje(e.target.value)}
            />
          </div>

          {invalidos.length > 0 && (
            <p className="text-xs text-rose-700">
              Correo mal escrito: {invalidos.join(', ')}
            </p>
          )}

          <p className="flex items-center gap-1.5 rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
            <Paperclip className="h-3 w-3" />
            Se adjunta la cotización en PDF, generada al momento de enviar.
          </p>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            Al enviarla, la oferta queda bloqueada. Para cambiarla se emite una versión
            nueva.
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t p-4">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!puedeEnviar || enviando}>
            {enviando ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
